import { useMutation, useQuery } from "@tanstack/react-query";
import { IError } from "@/shared/types";
import { IRequestApiError, requestApi } from "@/shared/utills";
import type { ICollaborator, IColleague, ISkillDeviationsResponse, IStepData } from "./model";

const ENDPOINTS = {
  EMPLOYEE_LIST: "/new-assessments/employee-list",
  COLLABORATOR_LIST: "/new-assessments/collaborator-list",
  CREATE_ASSESSMENT: "/new-assessments/create-assessment",
  CONTINUE_ASSESSMENT: "/new-assessments/continue-assessment",
  SUBMIT_ASSESSMENT: "/new-assessments/submit-assessment",
  NEXT_STEP_DATA: "/new-assessments/next-step-data",
  PREV_STEP_DATA: "/new-assessments/prev-step-data",
  SUBMIT_STEP_DATA: "/new-assessments/submit-step-data",
  SKILL_DEVIATIONS: "/new-assessments/skill-deviations",
};

const QUERY_KEYS = {
  COLLABORATOR_LIST: "collaboratorList",
  EMPLOYEE_LIST: "employeesList",
  NEXT_STEP_DATA: "nextStepData",
  PREV_STEP_DATA: "prevStepData",
  SKILL_DEVIATIONS: "skillDeviations",
};

export const useGetCollaboratorList = (enabled: boolean) => {
  return useQuery({
    queryKey: [QUERY_KEYS.COLLABORATOR_LIST],
    queryFn: async () => {
      const response = await requestApi<ICollaborator[]>({
        method: "GET",
        path: ENDPOINTS.COLLABORATOR_LIST,
      });

      return response;
    },
    enabled,
  });
};

export const useGetColleagueList = (enabled: boolean) => {
  return useQuery({
    queryKey: [QUERY_KEYS.EMPLOYEE_LIST],
    queryFn: async () => {
      const response = await requestApi<IColleague[]>({
        method: "GET",
        path: ENDPOINTS.EMPLOYEE_LIST,
      });

      return response;
    },
    enabled,
  });
};

export const useCreateAssessment = () => {
  return useMutation<{ assessmentId: number }, IRequestApiError<IError>, { assessedFmno: string }>({
    mutationFn: async (data) => {
      const response = await requestApi<{ assessmentId: number }>({
        method: "POST",
        path: ENDPOINTS.CREATE_ASSESSMENT,
        body: data,
      });

      return response;
    },
  });
};

interface IContinueAssessmentRequest {
  assessedFmno: string;
  assessmentId: number;
}

export const useContinueAssessment = () => {
  return useMutation<unknown, IRequestApiError<IError>, IContinueAssessmentRequest>({
    mutationFn: async (data) => {
      const response = await requestApi({
        method: "POST",
        path: ENDPOINTS.CONTINUE_ASSESSMENT,
        body: data,
      });

      return response;
    },
  });
};

export const useGetNextStepData = (assessmentId: number, stepId?: string) => {
  return useQuery<IStepData>({
    enabled: Boolean(assessmentId),
    queryKey: [QUERY_KEYS.NEXT_STEP_DATA, stepId],
    structuralSharing: false,
    queryFn: async () => {
      const response = await requestApi<IStepData>({
        method: "GET",
        path: ENDPOINTS.NEXT_STEP_DATA,
        queryParams: {
          assessmentId,
          ...(stepId && { stepId }),
        },
      });

      return response;
    },
  });
};

export const useGetPrevStepData = (assessmentId: number, stepId: string) => {
  return useQuery({
    enabled: Boolean(assessmentId && stepId !== stepId),
    queryKey: [QUERY_KEYS.PREV_STEP_DATA],
    structuralSharing: false,
    queryFn: async () => {
      const response = await requestApi<IStepData>({
        method: "GET",
        path: ENDPOINTS.PREV_STEP_DATA,
        queryParams: { assessmentId, stepId },
      });

      return response;
    },
  });
};

interface ISendStepRequestData {
  stepId: string;
  data: any;
  comment?: string;
}

interface ISendStepResponseData {
  assessmentStatus: "in_progress" | "submitted";
}

export const useSendStepData = () => {
  return useMutation({
    mutationFn: async ({ data, assessmentId }: { data: ISendStepRequestData; assessmentId: number }) => {
      const response = await requestApi<ISendStepResponseData>({
        method: "POST",
        path: ENDPOINTS.SUBMIT_STEP_DATA,
        body: data,
        queryParams: { assessmentId },
      });

      return response;
    },
  });
};

export const useGetSkillDeviations = (assessmentId: number) => {
  return useQuery<ISkillDeviationsResponse>({
    enabled: !!assessmentId,
    queryKey: [QUERY_KEYS.SKILL_DEVIATIONS, assessmentId],
    queryFn: async () => {
      const response = await requestApi<ISkillDeviationsResponse>({
        method: "GET",
        path: ENDPOINTS.SKILL_DEVIATIONS,
        queryParams: { assessmentId },
      });

      return response;
    },
  });
};
